// /admin/ads → "קריאייטיבים": every video FBZipper can advertise with (the reels + videos of
// ads that already ran), with each one's past results summed over every ad that used it.
// Tick videos to put them in an A/B test, or launch a single ad from any card.
import {FC, useEffect, useState} from 'react'
import {Creative, getCreatives, getVideoSource} from './_requests'
import LaunchForm from './LaunchForm'

const ils = (n: number | null | undefined) =>
  n == null ? '—' : `₪${Math.round(n).toLocaleString('he-IL')}`
const errMsg = (e: any) => e?.response?.data?.error || e?.message || 'שגיאה'
const mmss = (s?: number) =>
  s
    ? s >= 60
      ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`
      : `${Math.round(s)} שנ׳`
    : ''

const Creatives: FC<{
  selected: string[]
  setSelected: (k: string[]) => void
  defaultLink: string
  lookalikes: string[]
  onLaunched: () => void
  goTests: () => void
}> = ({selected, setSelected, defaultLink, lookalikes, onLaunched, goTests}) => {
  const [items, setItems] = useState<Creative[] | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [playing, setPlaying] = useState<Record<string, string>>({})
  const [filter, setFilter] = useState<'all' | 'proven' | 'reel' | 'video'>('all')

  const load = async (refresh = false) => {
    setBusy(true)
    setError('')
    try {
      const {data} = await getCreatives(refresh)
      setItems(data.creatives)
      // A refresh rebuilds in the background (~30s of Facebook calls) — read it again after.
      if (data.rebuilding) setTimeout(() => load(false), 45000)
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusy(false)
    }
  }
  useEffect(() => {
    load()
  }, [])

  const play = async (c: Creative) => {
    if (c.mp4) return setPlaying({...playing, [c.key]: c.mp4})
    try {
      const {data} = await getVideoSource(c.video_id!)
      if (data.source) setPlaying({...playing, [c.key]: data.source})
    } catch (e) {
      setError(errMsg(e))
    }
  }

  const toggle = (k: string) =>
    setSelected(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k])

  // Cheapest cost per lead among creatives with real volume (≥ 10 leads).
  const ranked = (items || [])
    .filter((c) => c.stats.leads >= 10 && c.stats.cpl != null)
    .sort((a, b) => a.stats.cpl! - b.stats.cpl!)
  const topKeys = ranked.slice(0, 3).map((c) => c.key)

  const shown = (items || []).filter((c) =>
    filter === 'all' ? true : filter === 'proven' ? c.stats.leads > 0 : c.kind === filter
  )

  return (
    <div style={{paddingBottom: selected.length ? 80 : 0}}>
      <div className='d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3'>
        <div className='text-muted fs-7'>
          כל הסרטונים — הרילס החדשים וסרטונים ממודעות קודמות — עם התוצאות שלהם מכל המודעות שרצו
          איתם. סמנו ✓ את מה שרוצים לבדוק, או צרו מודעה בודדת.
        </div>
        <div className='d-flex gap-2'>
          <div className='btn-group btn-group-sm'>
            {(
              [
                ['all', 'הכל'],
                ['proven', 'עם תוצאות'],
                ['reel', 'רילס חדשים'],
                ['video', 'סרטונים קודמים'],
              ] as const
            ).map(([k, l]) => (
              <button
                key={k}
                className={`btn ${filter === k ? 'btn-primary' : 'btn-light'}`}
                onClick={() => setFilter(k)}
              >
                {l}
              </button>
            ))}
          </div>
          <button className='btn btn-sm btn-light' disabled={busy} onClick={() => load(true)}>
            {busy ? 'טוען…' : 'רענון נתונים'}
          </button>
        </div>
      </div>
      {error && <div className='alert alert-danger py-2 fs-7'>{error}</div>}
      {!items && !error && <div className='text-muted'>טוען את הסרטונים…</div>}

      <div
        className='d-grid gap-4'
        style={{gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))'}}
      >
        {shown.map((c) => {
          const sel = selected.includes(c.key)
          const top = topKeys.indexOf(c.key)
          return (
            <div
              key={c.key}
              className='card card-bordered p-3'
              style={sel ? {outline: '3px solid #2b70fa'} : undefined}
            >
              <div style={{position: 'relative'}}>
                {playing[c.key] ? (
                  <video
                    src={playing[c.key]}
                    controls
                    autoPlay
                    playsInline
                    style={{
                      width: '100%',
                      aspectRatio: '9 / 16',
                      borderRadius: 10,
                      background: '#000',
                    }}
                  />
                ) : (
                  <div
                    onClick={() => play(c)}
                    style={{
                      width: '100%',
                      aspectRatio: '9 / 16',
                      borderRadius: 10,
                      background: `#000 url("${c.thumb}") center/cover no-repeat`,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span
                      style={{fontSize: 44, color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,.6)'}}
                    >
                      ▶
                    </span>
                  </div>
                )}
                <label
                  className='form-check'
                  style={{
                    position: 'absolute',
                    top: 8,
                    insetInlineStart: 8,
                    background: '#fff',
                    borderRadius: 8,
                    padding: '4px 8px 4px 30px',
                    margin: 0,
                  }}
                >
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={sel}
                    onChange={() => toggle(c.key)}
                  />
                  <span className='form-check-label fs-8 fw-bold'>לבדיקה</span>
                </label>
                {top !== -1 && (
                  <span
                    className='badge badge-success'
                    style={{position: 'absolute', top: 8, insetInlineEnd: 8}}
                  >
                    🏆 #{top + 1} עלות לליד
                  </span>
                )}
              </div>
              <div className='d-flex justify-content-between align-items-baseline mt-2 gap-2'>
                <b className='text-truncate'>{c.title || 'סרטון'}</b>
                <span className='text-muted fs-8 text-nowrap'>
                  {c.kind === 'reel' ? 'רילס' : 'קודם'} · {mmss(c.length)}
                </span>
              </div>
              {c.concept && <div className='text-muted fs-8'>{c.concept}</div>}
              <div className='d-grid gap-1 mt-2 fs-8' style={{gridTemplateColumns: '1fr 1fr 1fr'}}>
                <div>
                  <div className='text-muted'>הוצאה</div>
                  <b>{ils(c.stats.spend)}</b>
                </div>
                <div>
                  <div className='text-muted'>לידים</div>
                  <b>{c.stats.leads || '—'}</b>
                </div>
                <div>
                  <div className='text-muted'>עלות לליד</div>
                  <b className={top !== -1 ? 'text-success' : ''}>{ils(c.stats.cpl)}</b>
                </div>
                <div>
                  <div className='text-muted'>מודעות</div>
                  <b>{c.stats.ads}</b>
                </div>
                <div>
                  <div className='text-muted'>CTR</div>
                  <b>{c.stats.ctr != null ? c.stats.ctr.toFixed(1) + '%' : '—'}</b>
                </div>
                <div>
                  <div className='text-muted'>עלות לקליק</div>
                  <b>{ils(c.stats.cpc)}</b>
                </div>
              </div>
              <button
                className={`btn btn-sm mt-3 ${open === c.key ? 'btn-light' : 'btn-light-primary'}`}
                onClick={() => setOpen(open === c.key ? null : c.key)}
              >
                {open === c.key ? 'סגור' : '🚀 מודעה בודדת מהסרטון'}
              </button>
              {open === c.key && (
                <LaunchForm
                  creative={{key: c.key, headline: c.headline || '', text: c.text || ''}}
                  defaultLink={defaultLink}
                  lookalikes={lookalikes}
                  onDone={() => {
                    setOpen(null)
                    onLaunched()
                  }}
                />
              )}
            </div>
          )
        })}
      </div>

      {selected.length > 0 && (
        <div
          className='d-flex align-items-center gap-3 flex-wrap p-4 shadow'
          style={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#1e293b',
            color: '#fff',
            borderRadius: 14,
            zIndex: 50,
          }}
        >
          <b>נבחרו {selected.length} סרטונים לבדיקה</b>
          <button className='btn btn-sm btn-light' onClick={() => setSelected([])}>
            ניקוי
          </button>
          <button className='btn btn-sm btn-primary' onClick={goTests}>
            המשך לבניית הבדיקה ←
          </button>
        </div>
      )}
    </div>
  )
}

export default Creatives
